import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '@/lib/api';

export type FieldType = 'string' | 'number' | 'money' | 'minutes' | 'date' | 'enum' | 'boolean';

export type ReportField = {
  key: string;
  label: string;
  type: FieldType;
  groupable: boolean;
  aggregatable: boolean;
};

export type ReportSource = {
  key: string;
  label: string;
  description: string;
  fields: ReportField[];
};

export type FilterOperator =
  | 'eq' | 'neq' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'in' | 'is_empty' | 'is_not_empty' | 'between';

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  in: 'is one of',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  between: 'is between',
};

/** Which comparisons make sense for which kind of column. */
export function operatorsFor(type: FieldType): FilterOperator[] {
  switch (type) {
    case 'money':
    case 'number':
    case 'minutes':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'];
    case 'date':
      return ['eq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'];
    case 'boolean':
      return ['eq'];
    case 'enum':
      return ['eq', 'neq', 'in', 'is_empty', 'is_not_empty'];
    default:
      return ['contains', 'eq', 'neq', 'in', 'is_empty', 'is_not_empty'];
  }
}

export type ReportFilter = {
  field: string;
  operator: FilterOperator;
  value?: unknown;
  value2?: unknown;
};

export type Aggregate = { field: string; fn: 'sum' | 'avg' | 'min' | 'max' | 'count' };

export type ReportDefinition = {
  dataSource: string;
  fields: string[];
  filters?: ReportFilter[];
  groupBy?: string;
  aggregates?: Aggregate[];
  sort?: string;
  limit?: number;
};

export type ReportResult = {
  source: { key: string; label: string };
  columns: { key: string; label: string; type: FieldType }[];
  rows: Record<string, unknown>[];
  groups: ({ key: string; count: number } & Record<string, string | number>)[] | null;
  totals: Record<string, string | number>;
  meta: { total: number; returned: number; truncated: boolean; cap: number };
};

export type SavedReport = {
  id: string;
  name: string;
  description: string | null;
  dataSource: string;
  fields: string[];
  filters: ReportFilter[] | null;
  groupBy: string | null;
  aggregates: Aggregate[] | null;
  sort: string | null;
  chartType: string;
  isStandard: boolean;
  updatedAt: string;
  owner?: { id: string; name: string } | null;
  schedules?: { id: string; frequency: string; isActive: boolean; nextRunAt: string | null }[];
};

export type WidgetData =
  | { kind: 'number'; value: number; unit?: string; hint?: string }
  | { kind: 'money'; value: string; currency: string; hint?: string }
  | { kind: 'percent'; value: number; hint?: string }
  | { kind: 'series'; points: { label: string; value: number }[]; unit?: string }
  | { kind: 'list'; rows: Record<string, unknown>[] }
  | null;

export type Widget = {
  id: string;
  kind: 'kpi' | 'chart' | 'list' | 'report';
  title: string;
  metric?: string;
  reportId?: string;
  chartType?: string;
  width: number;
  height: number;
  order: number;
  data: WidgetData;
  error: string | null;
};

export type Dashboard = {
  id: string;
  name: string;
  roleKey: string | null;
  isDefault: boolean;
  ownerId: string | null;
  widgets: Widget[];
};

export function useReportSources() {
  return useQuery({
    queryKey: ['reports', 'sources'],
    queryFn: () => apiGet<ReportSource[]>('/reports/sources'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSavedReports() {
  return useQuery({
    queryKey: ['reports', 'saved'],
    queryFn: () => apiGet<SavedReport[]>('/reports'),
  });
}

export function useSavedReport(id: string | undefined) {
  return useQuery({
    queryKey: ['reports', 'saved', id],
    queryFn: () => apiGet<SavedReport>(`/reports/${id}`),
    enabled: Boolean(id),
  });
}

export function useRunReport() {
  return useMutation({
    mutationFn: (definition: ReportDefinition) => apiPost<ReportResult>('/reports/run', definition),
  });
}

export function useSaveReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input: ReportDefinition & {
        id?: string;
        name: string;
        description?: string;
        chartType?: string;
      },
    ) => apiPost<SavedReport>('/reports', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/reports/${id}`);
      return { id };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
}

/**
 * Downloads a report as CSV.
 *
 * Posted rather than linked, because the definition can be long and a browser
 * download cannot carry an Authorization header on a plain link. The blob is
 * built here and handed to a temporary anchor.
 */
export async function downloadReportCsv(definition: ReportDefinition, filename: string): Promise<void> {
  const csv = await apiPost<string>('/reports/export', definition);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function useDashboards() {
  return useQuery({
    queryKey: ['dashboards'],
    queryFn: () =>
      apiGet<
        { id: string; name: string; roleKey: string | null; isDefault: boolean; ownerId: string | null }[]
      >('/dashboards'),
  });
}

export function useMyDashboard(filters: { from?: string; to?: string; ownerId?: string } = {}) {
  return useQuery({
    queryKey: ['dashboards', 'mine', filters],
    queryFn: () => apiGet<Dashboard>('/dashboards/mine', { params: filters }),
  });
}

export function useDashboard(
  id: string | undefined,
  filters: { from?: string; to?: string; ownerId?: string } = {},
) {
  return useQuery({
    queryKey: ['dashboards', id, filters],
    queryFn: () => apiGet<Dashboard>(`/dashboards/${id}`, { params: filters }),
    enabled: Boolean(id),
  });
}

export function useSaveDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      name: string;
      widgets: Omit<Widget, 'data' | 'error'>[];
      personal?: boolean;
    }) => apiPost<Dashboard>('/dashboards', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboards'] }),
  });
}

export function useMetricHistory(metric: string | undefined, days = 90) {
  return useQuery({
    queryKey: ['dashboards', 'metric', metric, days],
    queryFn: () =>
      apiGet<{ date: string; value: number; kind: string }[]>('/dashboards/metric', {
        params: { metric, days },
      }),
    enabled: Boolean(metric),
  });
}

export function useReportSchedules() {
  return useQuery({
    queryKey: ['reports', 'schedules'],
    queryFn: () =>
      apiGet<
        {
          id: string;
          frequency: string;
          dayOfPeriod: number;
          hourOfDay: number;
          recipientUserIds: string[];
          recipientEmails: string[];
          format: string;
          isActive: boolean;
          lastRunAt: string | null;
          nextRunAt: string | null;
          lastError: string | null;
          report: { id: string; name: string };
        }[]
      >('/reports/schedules'),
  });
}

export function useSaveSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      reportId: string;
      frequency: string;
      dayOfPeriod: number;
      hourOfDay: number;
      recipientUserIds?: string[];
      recipientEmails?: string[];
      isActive?: boolean;
    }) => apiPost('/reports/schedules', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/reports/schedules/${id}`);
      return { id };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
}
